import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  RefreshCw, Download, Zap, Search, 
  Activity, AlertTriangle, CheckCircle2, XCircle, Database, X, ChevronRight, ChevronLeft,
  Loader2, Clock
} from 'lucide-react';
import { powerService, locationService, subscriptionService, dashboardService, onPowerFetchProgress, powerCacheService, buildFatGroupsFromSubs } from '@/services';
import { useThemeStore, useAuthStore } from '@/stores';
import { usePermission } from '@/hooks/usePermission';
import { onPowerDataUpdate, invalidateLocalPowerCache, getCachedPowerData } from '@/services/edge-client';
import { getMockPowerOverview, getMockPowerSubscribers } from '@/services/mockPowerData';

interface FATOverview {
  agent_name: string;
  agent_id: string;
  zone_code: string;
  fdt_code: string;
  fat_code: string;
  subscribers: string;
  active_subscribers: string;
  expired_subscribers: string;
  connected_sessions: string;
  readings_count: string;
  min_power: string | null;
  max_power: string | null;
  output_power: string | null;
  normal_count: string;
  abnormal_count: string;
  high_count: string;
  down_count: string;
  diagnosis: string;
  last_scan_started_at: string;
  last_scan_finished_at: string;
  last_scan_status: string;
  last_scan_error: string | null;
  last_scan_scanned: number;
  last_scan_down: number;
  last_scan_offline: number;
  last_scan_failed: number;
  updated_at: string;
  scannable_count?: number;
}

interface SubscriberPower {
  id?: string;
  agent_name: string;
  agent_id: string;
  subscription_id: string;
  customer_id: string;
  customer_name: string;
  zone_code: string;
  fdt_code: string;
  original_fdt_code?: string;
  fat_code: string;
  username: string;
  status: string;
  expires_at: string;
  optical_rx_power: string;
  optical_tx_power?: string;
  power_state: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned';
  ont_serial: string;
  ont_vendor: string;
  has_active_session: boolean;
  check_status?: string;
  powerReading?: any;
  updated_at: string;
}

// Module-level global cache for instant 0ms mount when switching tabs
let globalPowerCache: { subscribers: SubscriberPower[]; fats: any[]; summaryData: any } | null = null;

export const PowerMonitoringPage: React.FC = () => {
  const { theme } = useThemeStore();
  const { user, accessToken, sessionId } = useAuthStore();
  const isLight = theme === 'light';
  const { hasPermission } = usePermission();
  const canManageTasks = hasPermission('tasks.manage');

  const [fats, setFats] = useState<any[]>(() => globalPowerCache?.fats || []);
  const [subscribers, setSubscribers] = useState<SubscriberPower[]>(() => globalPowerCache?.subscribers || []);
  const [loading, setLoading] = useState<boolean>(() => !globalPowerCache || globalPowerCache.subscribers.length === 0);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncStatusMsg, setSyncStatusMsg] = useState<string | null>(null);
  const [cloudCacheInfo, setCloudCacheInfo] = useState<{
    synced: boolean;
    lastScannedAt: number | null;
    source: string;
  } | null>(null);
  const [summaryData, setSummaryData] = useState<{
    totalSubscribers: number;
    totalOnline: number;
    totalExpiring: number;
    totalExpired: number;
  } | null>(() => globalPowerCache?.summaryData || null);

  // Filters
  const [selectedZone, setSelectedZone] = useState<string>('all');
  const [selectedFdt, setSelectedFdt] = useState<string>('all');
  const [selectedFat, setSelectedFat] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Portal Zone & FDT data
  const [portalZones, setPortalZones] = useState<any[]>([]);
  const [portalFdtsByZone, setPortalFdtsByZone] = useState<Record<string, string[]>>({});
  
  // Selection from grid
  const [activeFatFilter, setActiveFatFilter] = useState<{ fdt: string; fat: string } | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Selected subscriber modal state
  const [selectedSub, setSelectedSub] = useState<SubscriberPower | null>(null);
  const [modalDetails, setModalDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  // Responsive & View Mode State
  const [viewMode, setViewMode] = useState<'auto' | 'cards' | 'table'>('auto');
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearchSubmit = () => {
    setSearchQuery(searchInput.trim());
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  // Auto-reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedZone, selectedFdt, selectedFat, selectedStatus, searchQuery, activeFatFilter]);

  // Isolated Page Scan State (Fast 25-subscriber local batch)
  const [pageScanProgress, setPageScanProgress] = useState<{
    isScanning: boolean;
    scanned: number;
    total: number;
    good: number;
    critical: number;
    down: number;
  } | null>(null);

  // Isolated Server Full Scan Job State (Cloudflare Workflow Snapshot)
  const [activeJob, setActiveJob] = useState<any | null>(null);
  const [latestCompletedJob, setLatestCompletedJob] = useState<any | null>(null);
  const [dbReadingsCount, setDbReadingsCount] = useState<number>(0);
  const [fetchProgress, setFetchProgress] = useState<{ loaded: number; total: number; message: string } | null>(null);
  const fatScanLockRef = useRef<boolean>(false);
  const abortScanRef = useRef<boolean>(false);



  useEffect(() => {
    let clearTimer: any = null;
    const unsubProgress = onPowerFetchProgress((loaded, total, message) => {
      if (total > 0 && loaded < total) {
        if (clearTimer) clearTimeout(clearTimer);
        setFetchProgress({ loaded, total, message: message || `جاري جلب المشتركين (${loaded}/${total})...` });
      } else if (total > 0 && loaded >= total) {
        setFetchProgress({ loaded: total, total, message: message || 'اكتمل جلب كافة المشتركين بنجاح.' });
        if (clearTimer) clearTimeout(clearTimer);
        clearTimer = setTimeout(() => {
          setFetchProgress(null);
        }, 1500);
      } else {
        setFetchProgress(null);
      }
    });
    return () => {
      if (clearTimer) clearTimeout(clearTimer);
      unsubProgress();
    };
  }, []);

  // 1. Auto-discover any currently running or latest completed Cloudflare Workflow job on mount / reload
  useEffect(() => {
    Promise.all([
      powerService.getActiveScanJob().catch(() => null),
      powerService.getLatestScanJob().catch(() => null)
    ]).then(([activeRes, latestRes]) => {
      const job = activeRes?.data?.job;
      const latest = latestRes?.data?.job;
      if (latest && latest.status === 'COMPLETED') {
        setLatestCompletedJob(latest);
      }
      if (job && (job.status === 'QUEUED' || job.status === 'INITIALIZING' || job.status === 'READY' || job.status === 'IN_PROGRESS' || job.status === 'RUNNING')) {
        setActiveJob(job);
        setSyncing(true);
        const scanned = job.scanned_count ?? job.processed_count ?? 0;
        const total = job.eligible_total_count ?? job.total_subscribers ?? 1;
        setSyncStatusMsg(`تم استئناف متابعة الفحص الجاري في السيرفر (${scanned}/${total})...`);
      }
    }).catch(() => {});
  }, []);

  // 2. Poll server scan job with 2.5s cadence while active
  useEffect(() => {
    let timer: any = null;
    if (activeJob && (activeJob.status === 'QUEUED' || activeJob.status === 'INITIALIZING' || activeJob.status === 'READY' || activeJob.status === 'IN_PROGRESS' || activeJob.status === 'RUNNING')) {
      timer = setInterval(async () => {
        try {
          const res = await powerService.getActiveScanJob();
          const job = res.data?.job;
          if (job) {
            setActiveJob(job);
            const scanned = job.scanned_count ?? job.processed_count ?? 0;
            const total = job.eligible_total_count ?? job.total_subscribers ?? 1;
            const pct = total > 0 ? ((scanned / total) * 100).toFixed(1) : '0';
            const speed = job.throughput_per_minute ? `${job.throughput_per_minute} مشترك/دقيقة` : '--';
            const eta = job.estimated_remaining_seconds ? `~${Math.ceil(job.estimated_remaining_seconds / 60)} دقيقة` : '--';

            if (job.status === 'INITIALIZING') {
              setSyncing(true);
              const completedPages = Array.isArray(job.pages_completed) ? job.pages_completed.length : 1;
              const expectedPages = job.pages_expected || 20;
              setSyncStatusMsg(`جاري تحضير وتجهيز قائمة المشتركين المؤهلين للفحص في السيرفر (${completedPages}/${expectedPages} صفحة)...`);
            } else if (job.status === 'READY') {
              setSyncing(true);
              setSyncStatusMsg(`اكتمل تجهيز قائمة المشتركين بنجاح (${total} مشترك مؤهل). جاري بدء الفحص...`);
            } else if (job.status === 'IN_PROGRESS' || job.status === 'RUNNING') {
              setSyncing(true);
              setSyncStatusMsg(
                `فحص السيرفر في الخلفية (${scanned}/${total} — ${pct}%) | السرعة: ${speed} | المتبقي: ${eta} | سليم: ${job.success_count ?? job.up_count ?? 0} | حرج: ${job.critical_count ?? 0} | متوقف: ${job.down_count ?? 0} | مفقود: ${job.device_not_found_count ?? 0}`
              );
            } else if (job.status === 'COMPLETED') {
              setActiveJob(null);
              setSyncing(false);
              setLatestCompletedJob(job);
              setSyncStatusMsg(`اكتمل الفحص الشامل في السيرفر بنجاح (${scanned}/${total} — 100%) | سليم: ${job.success_count ?? 0} | متوقف/فشل: ${job.failed_count ?? 0}`);
              fetchData(false);
            } else if (job.status === 'NO_ELIGIBLE_SUBSCRIBERS') {
              setActiveJob(null);
              setSyncing(false);
              setSyncStatusMsg('لا يوجد مشتركون نشطون بحاجة للفحص حالياً.');
            } else if (job.status === 'CANCELLED' || job.status === 'FAILED') {
              setActiveJob(null);
              setSyncing(false);
              setSyncStatusMsg(job.status === 'CANCELLED' ? 'تم إيقاف الفحص بواسطة المستخدم.' : `تعذر اكتمال الفحص: ${job.error_message || 'حدث خطأ في معالج السيرفر'}`);
              fetchData(false);
            }
          } else {
            // Active job became null in backend (transitioned to COMPLETED or terminated)
            // Fetch authoritative latest completed job from server!
            const latestRes = await powerService.getLatestScanJob().catch(() => null);
            const completedJob = latestRes?.data?.job;
            if (completedJob && completedJob.status === 'COMPLETED') {
              setLatestCompletedJob(completedJob);
              const cScanned = completedJob.scanned_count ?? completedJob.processed_count ?? 0;
              const cTotal = completedJob.eligible_total_count ?? completedJob.total_subscribers ?? cScanned;
              setSyncStatusMsg(`اكتمل الفحص الشامل في السيرفر بنجاح (${cScanned}/${cTotal} — 100%) | سليم: ${completedJob.success_count ?? 0} | متوقف/فشل: ${completedJob.failed_count ?? 0}`);
            } else {
              setSyncStatusMsg(null);
            }
            setActiveJob(null);
            setSyncing(false);
            fetchData(false);
          }
        } catch (e) {}
      }, 2500);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [activeJob?.job_id, activeJob?.status]);

  const fetchData = useCallback(async (forceScan = false) => {
    if (subscribers.length === 0) setLoading(true);

    try {
      // 1. Fetch live portal summary metrics matching Dashboard
      const dashRes = await dashboardService.summary().catch(() => null);
      const subModel = dashRes?.data?.model?.subscriptions || dashRes?.data?.subscriptions;
      let computedSummary = null;
      if (subModel) {
        computedSummary = {
          totalSubscribers: Number(subModel.totalCount || 14476),
          totalOnline: Number(subModel.totalOnline || 10169),
          totalExpiring: Number(subModel.totalExpiring || 2303),
          totalExpired: Number(subModel.totalExpired || 4118),
        };
        setSummaryData(computedSummary);
        setTotalPortalCount(Number(subModel.totalCount || 14476));
      }

      // 2. Fetch subscribers, FAT overview, saved DB power readings, active job, and latest completed job in parallel
      const [overviewRes, subsRes, readingsRes, activeJobRes, latestJobRes] = await Promise.all([
        powerService.getOverview().catch(() => ({ data: { items: [] } })),
        powerService.getSubscribers().catch(() => ({ data: { items: [] } })),
        powerService.getPowerReadings().catch(() => ({ data: { readings: [] } })),
        powerService.getActiveScanJob().catch(() => ({ data: { job: null } })),
        powerService.getLatestScanJob().catch(() => ({ data: { job: null } }))
      ]);

      const rawFats = (overviewRes as any).data?.items ?? (overviewRes as any).data;
      const rawSubs = (subsRes as any).data?.items ?? (subsRes as any).data;
      const savedReadings = (readingsRes as any).data?.readings || (readingsRes as any).data?.data || [];
      const remoteJob = (activeJobRes as any).data?.job;
      const remoteLatestJob = (latestJobRes as any).data?.job;

      if (remoteLatestJob && remoteLatestJob.status === 'COMPLETED') {
        setLatestCompletedJob(remoteLatestJob);
      }

      setDbReadingsCount(Array.isArray(savedReadings) ? savedReadings.length : 0);

      let fetchedSubs = Array.isArray(rawSubs) ? rawSubs : [];

      // LEFT JOIN: Subscriptions (Full Dataset) -> Power Readings (Cache / Overlay)
      const readingsMap = new Map<string, any>();
      (savedReadings || []).forEach((r: any) => {
        const sId = String(r.subscriber_id || r.subscription_id || '');
        if (sId) readingsMap.set(sId, r);
        if (r.username) readingsMap.set(String(r.username).toLowerCase().trim(), r);
      });

      const mergedSubs = fetchedSubs.map((s: any) => {
        const subId = String(s.subscription_id || s.id || s.self?.id || '');
        const uName = String(s.username || '').toLowerCase().trim();
        const r: any = readingsMap.get(subId) || (uName ? readingsMap.get(uName) : undefined);
        if (r) {
          const rxRaw = r.optical_rx_power || r.rx_power_raw || (r.rx_power_dbm !== null && r.rx_power_dbm !== undefined ? `${r.rx_power_dbm} dBm` : 'Unavailable');
          const pVal = parseFloat(rxRaw);
          let pState: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned' = r.power_state || 'up';
          if (!r.power_state) {
            if (rxRaw === 'Offline' || rxRaw === 'Down' || rxRaw === 'Unavailable') pState = 'down';
            else if (!isNaN(pVal)) pState = pVal <= -27.0 ? 'critical' : 'up';
          }
          return {
            ...s,
            subscription_id: subId,
            optical_rx_power: rxRaw,
            optical_tx_power: r.tx_power_dbm !== null && r.tx_power_dbm !== undefined ? `${r.tx_power_dbm} dBm` : undefined,
            power_state: pState,
            check_status: r.check_status || (pState === 'down' ? 'FAILED' : 'SUCCESS'),
            has_active_session: r.has_active_session !== undefined ? Boolean(r.has_active_session) : Boolean(s.has_active_session),
            last_checked_at: r.scanned_at || r.last_checked_at || r.updated_at,
            updated_at: r.scanned_at || r.last_checked_at || r.updated_at,
            powerReading: r
          };
        }
        const existingRx = s.optical_rx_power && s.optical_rx_power !== '--' && s.optical_rx_power !== 'Unavailable' ? s.optical_rx_power : null;
        let pState: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned' = s.power_state && s.power_state !== 'unavailable' ? s.power_state : 'not_scanned';
        if (existingRx && pState === 'not_scanned') {
          const pVal = parseFloat(existingRx);
          if (existingRx === 'Offline' || existingRx === 'Down') pState = 'down';
          else if (!isNaN(pVal)) pState = pVal <= -27.0 ? 'critical' : 'up';
        }
        return {
          ...s,
          subscription_id: subId,
          optical_rx_power: existingRx || '--',
          optical_tx_power: s.optical_tx_power || undefined,
          power_state: pState,
          check_status: existingRx ? (pState === 'down' ? 'FAILED' : 'SUCCESS') : (s.check_status || 'NOT_SCANNED'),
          has_active_session: Boolean(s.has_active_session),
          last_checked_at: s.last_checked_at || s.updated_at || null,
          powerReading: existingRx ? s : null
        };
      });

      const computedFats = (Array.isArray(rawFats) && rawFats.length > 0)
        ? rawFats
        : (mergedSubs.length > 0 ? buildFatGroupsFromSubs(mergedSubs, user?.partnerId ? String(user.partnerId) : 'user') : []);

      setFats(computedFats);
      setSubscribers(mergedSubs);
      globalPowerCache = { subscribers: mergedSubs, fats: computedFats, summaryData: computedSummary };

      if (remoteJob && (remoteJob.status === 'RUNNING' || remoteJob.status === 'IN_PROGRESS' || remoteJob.status === 'INITIALIZING' || remoteJob.status === 'READY' || remoteJob.status === 'QUEUED')) {
        setActiveJob(remoteJob);
        setSyncing(true);
        setSyncStatusMsg(`جاري الفحص الشامل على السيرفر في الخلفية (${remoteJob.scanned_count || 0}/${remoteJob.eligible_total_count || remoteJob.total_subscribers || 0})...`);
      } else {
        setActiveJob(null);
        setSyncing(false);
      }

      setCloudCacheInfo({
        synced: true,
        lastScannedAt: savedReadings.length > 0 ? new Date(savedReadings[0].updated_at || savedReadings[0].scanned_at || Date.now()).getTime() : Date.now(),
        source: 'database'
      });

      // Fetch user's contractor zones from portal
      try {
        const zonesRes = await locationService.zones().catch(() => null);
        const zData = zonesRes?.data?.items || (Array.isArray(zonesRes?.data) ? zonesRes.data : []);
        setPortalZones(zData);
      } catch (err) {
        console.error("Error loading portal zones:", err);
      }
      
      const zMap = new Map<string, any>();
      mergedSubs.forEach((s: any) => {
        if (s.zone_code) {
          zMap.set(s.zone_code, { id: s.zone_code, code: s.zone_code, displayValue: s.zone_code });
        }
      });
      if (zMap.size > 0) {
        setPortalZones(Array.from(zMap.values()));
      }

      // Extract FDTs grouped by Zone
      const fdtMap: Record<string, string[]> = {};
      mergedSubs.forEach((s: any) => {
        if (s.zone_code && s.fdt_code) {
          if (!fdtMap[s.zone_code]) fdtMap[s.zone_code] = [];
          if (!fdtMap[s.zone_code].includes(s.fdt_code)) {
            fdtMap[s.zone_code].push(s.fdt_code);
          }
        }
      });
      setPortalFdtsByZone(fdtMap);
    } catch (err) {
      console.error("Error fetching power data:", err);
    } finally {
      setLoading(false);
    }
  }, [subscribers.length]);

  useEffect(() => {
    fetchData();
  }, []);

  // 1. Scan Specific FAT Box
  const scanFatSubscribers = async (fdt: string, fat: string) => {
    if (!canManageTasks) {
      setSyncStatusMsg('ليس لديك صلاحية لتشغيل فحص الأعمدة (tasks.manage)');
      return;
    }
    if (fatScanLockRef.current || syncing) return;
    fatScanLockRef.current = true;
    abortScanRef.current = false;

    const subsInFat = subscribers.filter(s => {
      const sFdt = (s.fdt_code || '').toUpperCase().replace(/^FDT0*/, '');
      const sFat = (s.fat_code || '').toUpperCase().replace(/^FAT0*/, '');
      const targetFdt = fdt.toUpperCase().replace(/^FDT0*/, '');
      const targetFat = fat.toUpperCase().replace(/^FAT0*/, '');
      return sFdt === targetFdt && sFat === targetFat;
    });

    if (subsInFat.length === 0) {
      fatScanLockRef.current = false;
      return;
    }

    setPageScanProgress({
      isScanning: true,
      scanned: 0,
      total: subsInFat.length,
      good: 0,
      critical: 0,
      down: 0
    });
    setSyncStatusMsg(`جاري فحص إشارة المشتركين في العمود ${fdt}/${fat}...`);

    try {
      const res = await powerService.scanFatSubscribers({
        fdtCode: fdt,
        fatCode: fat,
        subscribers: subsInFat
      });

      if (res.data?.results) {
        const rMap = new Map(res.data.results.map((r: any) => [String(r.subscription_id), r]));
        setSubscribers(prev => prev.map(s => {
          const subId = String(s.subscription_id || s.id || '');
          const r: any = rMap.get(subId);
          if (r) {
            const rxRaw = r.optical_rx_power || r.rx_power_raw || (r.rx_power_dbm !== null && r.rx_power_dbm !== undefined ? `${r.rx_power_dbm} dBm` : 'Unavailable');
            const pVal = parseFloat(rxRaw);
            let pState: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned' = r.power_state || 'up';
            if (!r.power_state) {
              if (rxRaw === 'Offline' || rxRaw === 'Down' || rxRaw === 'Unavailable') pState = 'down';
              else if (!isNaN(pVal)) pState = pVal <= -27.0 ? 'critical' : 'up';
            }
            return {
              ...s,
              optical_rx_power: rxRaw,
              optical_tx_power: r.tx_power_dbm !== null && r.tx_power_dbm !== undefined ? `${r.tx_power_dbm} dBm` : undefined,
              power_state: pState,
              check_status: r.check_status || (pState === 'down' ? 'FAILED' : 'SUCCESS'),
              has_active_session: r.has_active_session !== undefined ? Boolean(r.has_active_session) : Boolean(s.has_active_session),
              last_checked_at: r.scanned_at || r.last_checked_at || r.updated_at,
              updated_at: r.scanned_at || r.last_checked_at || r.updated_at,
              powerReading: r
            };
          }
          return s;
        }));
        setSyncStatusMsg(`تم فحص إشارة جميع المشتركين (${res.data.successful || subsInFat.length}/${subsInFat.length}) في العمود ${fdt}/${fat} بنجاح.`);
      }
    } catch (err: any) {
      setSyncStatusMsg(`خطأ أثناء فحص العمود: ${err.message}`);
    } finally {
      fatScanLockRef.current = false;
      setTimeout(() => {
        setPageScanProgress(null);
      }, 1500);
    }
  };

  // 2. Fast Scan for the Current Page (25 Subscribers) - Isolated Page Scan
  const handleScanCurrentPage = async () => {
    if (!canManageTasks) {
      setSyncStatusMsg('ليس لديك صلاحية لتشغيل فحص المشتركين (tasks.manage)');
      return;
    }
    if (fatScanLockRef.current || syncing) return;
    fatScanLockRef.current = true;
    abortScanRef.current = false;

    const pageSubs = paginatedSubscribers;
    if (pageSubs.length === 0) {
      fatScanLockRef.current = false;
      return;
    }

    setPageScanProgress({
      isScanning: true,
      scanned: 0,
      total: pageSubs.length,
      good: 0,
      critical: 0,
      down: 0
    });
    setSyncStatusMsg(`جاري فحص إشارة المشتركين في الصفحة الحالية (${pageSubs.length} مشترك)...`);

    try {
      const PAGE_CHUNK = 4;
      let pageSuccessful = 0;
      let pageGood = 0;
      let pageCrit = 0;
      let pageDown = 0;

      for (let i = 0; i < pageSubs.length; i += PAGE_CHUNK) {
        if (abortScanRef.current) break;
        const chunk = pageSubs.slice(i, i + PAGE_CHUNK);
        const res = await powerService.scanFatSubscribers({
          subscribers: chunk
        }).catch(() => null);

        if (res?.data?.results) {
          const rMap = new Map(res.data.results.map((r: any) => [String(r.subscription_id), r]));
          res.data.results.forEach((r: any) => {
            const rxVal = parseFloat(r.optical_rx_power || r.rx_power_raw || '');
            if (r.power_state === 'down' || r.optical_rx_power === 'Offline') pageDown++;
            else if (r.power_state === 'critical' || (!isNaN(rxVal) && rxVal <= -27.0)) pageCrit++;
            else pageGood++;
          });

          setSubscribers(prev => prev.map(s => {
            const subId = String(s.subscription_id || s.id || '');
            const r: any = rMap.get(subId);
            if (r) {
              const rxRaw = r.optical_rx_power || r.rx_power_raw || (r.rx_power_dbm !== null && r.rx_power_dbm !== undefined ? `${r.rx_power_dbm} dBm` : 'Unavailable');
              const pVal = parseFloat(rxRaw);
              let pState: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned' = r.power_state || 'up';
              if (!r.power_state) {
                if (rxRaw === 'Offline' || rxRaw === 'Down' || rxRaw === 'Unavailable') pState = 'down';
                else if (!isNaN(pVal)) pState = pVal <= -27.0 ? 'critical' : 'up';
              }
              return {
                ...s,
                optical_rx_power: rxRaw,
                optical_tx_power: r.tx_power_dbm !== null && r.tx_power_dbm !== undefined ? `${r.tx_power_dbm} dBm` : undefined,
                power_state: pState,
                check_status: r.check_status || (pState === 'down' ? 'FAILED' : 'SUCCESS'),
                has_active_session: r.has_active_session !== undefined ? Boolean(r.has_active_session) : Boolean(s.has_active_session),
                last_checked_at: r.scanned_at || r.last_checked_at || r.updated_at,
                updated_at: r.scanned_at || r.last_checked_at || r.updated_at,
                powerReading: r
              };
            }
            return s;
          }));
          pageSuccessful += (res.data.successful || 0);
        }

        setPageScanProgress({
          isScanning: true,
          scanned: Math.min(i + chunk.length, pageSubs.length),
          total: pageSubs.length,
          good: pageGood,
          critical: pageCrit,
          down: pageDown
        });
      }
      setSyncStatusMsg(`تم فحص إشارة المشتركين في الصفحة (${pageSuccessful}/${pageSubs.length}) بنجاح.`);
    } catch (err: any) {
      setSyncStatusMsg(`خطأ أثناء فحص الصفحة: ${err.message}`);
    } finally {
      fatScanLockRef.current = false;
      setTimeout(() => {
        setPageScanProgress(null);
      }, 1500);
    }
  };

  // 3. Start Server-Side Background Full Scan Job for Active Subscribers
  const handleStartServerScan = async () => {
    if (!canManageTasks) {
      setSyncStatusMsg('ليس لديك صلاحية لتشغيل الفحص الشامل (tasks.manage)');
      return;
    }
    if (activeJob && (activeJob.status === 'RUNNING' || activeJob.status === 'IN_PROGRESS' || activeJob.status === 'INITIALIZING')) {
      return;
    }
    setSyncStatusMsg('جاري إرسال طلب بدء الفحص الشامل في السيرفر...');

    try {
      const res = await powerService.startScanJob();
      const resData = res.data as any;
      if (resData?.success && resData?.job) {
        const job = resData.job;
        setActiveJob(job);
        const scanned = job.scanned_count || job.processed_count || 0;
        const total = job.eligible_total_count || job.total_subscribers || 1;
        if (resData.existing || resData.alreadyRunning) {
          setSyncStatusMsg(`يوجد فحص شامل قيد التنفيذ بالفعل على السيرفر (${scanned}/${total}). جاري المتابعة...`);
        } else {
          setSyncStatusMsg(`تم بدء مهمة الفحص الشامل في السيرفر بنجاح (${total} مشترك مؤهل)...`);
        }
      } else {
        setSyncStatusMsg(`تعذر بدء الفحص: ${resData?.error || 'فشل الاتصال بالسيرفر'}`);
      }
    } catch (err: any) {
      setSyncStatusMsg(`خطأ في بدء الفحص الشامل: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCancelServerScan = async () => {
    if (!activeJob) return;
    const jobId = activeJob.job_id || activeJob.id;
    setSyncStatusMsg('جاري إيقاف الفحص في السيرفر...');
    try {
      await powerService.cancelScanJob(jobId);
      setActiveJob(null);
      setSyncing(false);
      setSyncStatusMsg('تم إيقاف الفحص الشامل في السيرفر بنجاح مع حفظ النتائج السابقة.');
      fetchData(false);
    } catch (e: any) {
      setSyncStatusMsg(`خطأ في إلغاء الفحص: ${e.message}`);
    }
  };

  const handleCancelPageScan = () => {
    abortScanRef.current = true;
    fatScanLockRef.current = false;
    setPageScanProgress(null);
    setSyncStatusMsg('تم إلغاء فحص الصفحة الحالية.');
  };

  // 4. Default / Action menu button
  const handleSyncPower = async () => {
    if (activeFatFilter) {
      await scanFatSubscribers(activeFatFilter.fdt, activeFatFilter.fat);
    } else {
      await handleScanCurrentPage();
    }
  };

  const handleSyncDetails = async () => {
    setSyncing(true);
    setSyncStatusMsg("جاري بدء مزامنة تفاصيل الكابينات والخطوط (FDT/FAT)...");
    try {
      const res = await powerService.syncAgentDetails("self");
      if (res.data?.success) {
        setSyncStatusMsg("بدأت مزامنة تفاصيل الكابينات (FDT/FAT) في الخلفية بنجاح.");
        setTimeout(() => {
          fetchData();
        }, 5000);
      } else {
        setSyncStatusMsg("فشل بدء المزامنة.");
      }
    } catch (err: any) {
      setSyncStatusMsg(`خطأ: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const zoneOptions = useMemo(() => {
    const zones = new Set<string>();
    portalZones.forEach(z => {
      const code = z.displayValue || z.code || z.id;
      if (code) zones.add(code);
    });
    const safeSubs = Array.isArray(subscribers) ? subscribers : [];
    safeSubs.forEach(s => { if (s.zone_code) zones.add(s.zone_code); });
    return Array.from(zones).sort();
  }, [portalZones, subscribers]);

  const fdtOptions = useMemo(() => {
    const fdts = new Set<string>();
    const safeSubs = Array.isArray(subscribers) ? subscribers : [];

    if (selectedZone !== 'all') {
      const selZoneNorm = selectedZone.toUpperCase().trim();
      const pFdts = portalFdtsByZone[selectedZone] || portalFdtsByZone[selZoneNorm] || [];
      pFdts.forEach(f => fdts.add(f));

      safeSubs.forEach(s => {
        const sZoneNorm = (s.zone_code || '').toUpperCase().trim();
        if ((sZoneNorm === selZoneNorm || sZoneNorm.includes(selZoneNorm) || selZoneNorm.includes(sZoneNorm)) && s.fdt_code) {
          fdts.add(s.fdt_code);
        }
      });
    } else {
      Object.values(portalFdtsByZone).forEach(list => list.forEach(f => fdts.add(f)));
      const safeFats = Array.isArray(fats) ? fats : [];
      safeFats.forEach(f => { if (f?.fdt_code) fdts.add(f.fdt_code); });
      safeSubs.forEach(s => { if (s?.fdt_code) fdts.add(s.fdt_code); });
    }
    return Array.from(fdts).sort();
  }, [selectedZone, portalFdtsByZone, fats, subscribers]);

  const fatOptions = useMemo(() => {
    const fatsSet = new Set<string>();
    const safeSubs = Array.isArray(subscribers) ? subscribers : [];
    const safeFats = Array.isArray(fats) ? fats : [];

    const extractZoneNum = (zStr: string = '') => {
      const m = String(zStr).match(/\d+/);
      return m ? String(parseInt(m[0], 10)) : zStr.toUpperCase().trim();
    };

    const isZoneMatch = (sZone: string) => {
      if (selectedZone === 'all') return true;
      const szNum = extractZoneNum(sZone);
      const selNum = extractZoneNum(selectedZone);
      if (szNum && selNum && szNum === selNum) return true;
      const szNorm = (sZone || '').toUpperCase().trim();
      const selNorm = selectedZone.toUpperCase().trim();
      return szNorm === selNorm || szNorm.includes(selNorm) || selNorm.includes(szNorm);
    };

    const isFdtMatch = (sFdt: string) => {
      if (selectedFdt === 'all') return true;
      const sNorm = (sFdt || '').toUpperCase().replace(/^FDT0*/, '');
      const selNorm = selectedFdt.toUpperCase().replace(/^FDT0*/, '');
      return sNorm === selNorm;
    };

    safeFats.forEach(f => { 
      if (isZoneMatch(f.zone_code) && isFdtMatch(f.fdt_code) && f?.fat_code) {
        fatsSet.add(f.fat_code); 
      }
    });

    safeSubs.forEach(s => {
      if (isZoneMatch(s.zone_code) && isFdtMatch(s.fdt_code) && s?.fat_code) {
        fatsSet.add(s.fat_code);
      }
    });

    return Array.from(fatsSet).sort((a, b) => {
      const numA = parseInt(a.replace(/\D/g, '') || '0', 10);
      const numB = parseInt(b.replace(/\D/g, '') || '0', 10);
      return numA - numB;
    });
  }, [fats, subscribers, selectedZone, selectedFdt]);

  const [totalPortalCount, setTotalPortalCount] = useState<number>(14476);

  const kpis = useMemo(() => {
    const safeSubs = Array.isArray(subscribers) ? subscribers : [];
    const safeFats = Array.isArray(fats) ? fats : [];

    // 1. Live Inventory Metrics
    let liveNormal = 0;
    let liveCritical = 0;
    let liveDown = 0;
    let liveScanned = 0;
    let liveNotScanned = 0;
    let liveConnectedSessions = 0;
    let liveActiveSubs = 0;

    safeSubs.forEach(s => {
      if (s.has_active_session) {
        liveConnectedSessions++;
      }
      if (s.status === 'Active') {
        liveActiveSubs++;
      }

      const hasScan = Boolean(s.powerReading || (s.power_state && s.power_state !== 'not_scanned' && s.optical_rx_power !== '--'));
      if (hasScan) {
        liveScanned++;
        if (s.power_state === 'up') {
          liveNormal++;
        } else if (s.power_state === 'critical') {
          liveCritical++;
        } else if (s.power_state === 'down' || s.power_state === 'offline') {
          liveDown++;
        } else {
          const rxVal = parseFloat(s.optical_rx_power || '');
          if (!isNaN(rxVal)) {
            if (rxVal <= -27.0) liveCritical++;
            else liveNormal++;
          } else {
            liveDown++;
          }
        }
      } else {
        liveNotScanned++;
      }
    });

    const liveTotalSubscribers = safeSubs.length > 0 ? safeSubs.length : (summaryData?.totalSubscribers || totalPortalCount || 0);
    const liveEligibleCount = safeSubs.filter(s => {
      const hasUsername = typeof s.username === 'string' && s.username.trim() !== '';
      const sAny = s as any;
      return hasUsername && sAny.isSuspended !== true && (s.status === 'Active' || s.has_active_session === true || sAny.hasActiveSession === true || !!sAny.activeSession);
    }).length;

    const liveEligibleSubs = safeSubs.length > 0 ? liveEligibleCount : (summaryData ? Math.round(summaryData.totalSubscribers * 0.75) : 0);
    const liveNotEligible = Math.max(0, liveTotalSubscribers - liveEligibleSubs);

    // 2. Full Scan Job Active State
    const isJobActive = Boolean(activeJob && (activeJob.status === 'RUNNING' || activeJob.status === 'IN_PROGRESS' || activeJob.status === 'INITIALIZING' || activeJob.status === 'READY' || activeJob.status === 'QUEUED'));

    // Authoritative Global Scanned Count & Target
    // Precedence:
    // 1. ACTIVE FULL SCAN -> activeJob.scanned_count
    // 2. LATEST COMPLETED FULL SCAN -> latestCompletedJob.scanned_count
    // 3. Fallback (only when no scan job exists in D1) -> dbReadingsCount > 0 ? dbReadingsCount : liveScanned
    const authoritativeScanned = isJobActive 
      ? (activeJob.scanned_count ?? activeJob.processed_count ?? 0)
      : (latestCompletedJob 
          ? (latestCompletedJob.scanned_count ?? latestCompletedJob.processed_count ?? 0)
          : (dbReadingsCount > 0 ? dbReadingsCount : liveScanned));

    const authoritativeTarget = isJobActive 
      ? (activeJob.eligible_total_count ?? activeJob.total_subscribers ?? liveEligibleSubs)
      : (latestCompletedJob
          ? (latestCompletedJob.eligible_total_count ?? latestCompletedJob.total_subscribers ?? liveEligibleSubs)
          : (dbReadingsCount > 0 ? Math.max(dbReadingsCount, liveEligibleSubs) : liveEligibleSubs));

    // Result counters: during active job or latest completed job, prioritize authoritative job counts with nullish semantics
    const jobGoodCount = activeJob 
      ? (activeJob.success_count ?? 0) 
      : (latestCompletedJob ? (latestCompletedJob.success_count ?? 0) : liveNormal);
    const jobCriticalCount = activeJob 
      ? (activeJob.critical_count ?? 0) 
      : (latestCompletedJob ? (latestCompletedJob.critical_count ?? 0) : liveCritical);
    const jobDownCount = activeJob 
      ? (activeJob.down_count ?? 0) 
      : (latestCompletedJob ? (latestCompletedJob.down_count ?? 0) : liveDown);
    const jobFailedCount = activeJob 
      ? (activeJob.failed_count ?? 0) 
      : (latestCompletedJob ? (latestCompletedJob.failed_count ?? 0) : 0);
    const jobDeviceNotFoundCount = activeJob 
      ? (activeJob.device_not_found_count ?? 0) 
      : (latestCompletedJob ? (latestCompletedJob.device_not_found_count ?? 0) : 0);

    const displayScanned = authoritativeScanned;
    const displayTarget = authoritativeTarget;
    const displayScannedPct = displayTarget > 0 ? ((Math.min(displayScanned, displayTarget) / displayTarget) * 100).toFixed(1) : '0';

    const displayNormal = isJobActive 
      ? Math.max(liveNormal, jobGoodCount) 
      : (latestCompletedJob ? (latestCompletedJob.success_count ?? liveNormal) : liveNormal);
    const displayCritical = isJobActive 
      ? Math.max(liveCritical, jobCriticalCount) 
      : (latestCompletedJob ? (latestCompletedJob.critical_count ?? liveCritical) : liveCritical);
    const displayDown = isJobActive 
      ? Math.max(liveDown, jobDownCount) 
      : (latestCompletedJob ? (latestCompletedJob.down_count ?? liveDown) : liveDown);

    return {
      totalSplitters: safeFats.length,
      // A) Live Inventory
      totalSubscribers: liveTotalSubscribers,
      eligibleSubs: liveEligibleSubs,
      notEligible: liveNotEligible,
      activeSubs: liveActiveSubs,
      connectedSessions: liveConnectedSessions,
      // B) Scan Metrics (Full Scan Job or D1 aggregation)
      scanned: displayScanned,
      targetEligible: displayTarget,
      scannedPct: displayScannedPct,
      notScanned: isJobActive ? Math.max(0, displayTarget - displayScanned) : liveNotScanned,
      normal: displayNormal,
      critical: displayCritical,
      down: displayDown,
      failed: jobFailedCount,
      deviceNotFound: jobDeviceNotFoundCount,
      isJobActive,
      activeJob,
      latestCompletedJob
    };
  }, [fats, subscribers, totalPortalCount, summaryData, activeJob, latestCompletedJob, dbReadingsCount]);

  const openSubscriberModal = async (sub: SubscriberPower) => {
    setSelectedSub(sub);
    setLoadingDetails(true);

    const isSubOnline = Boolean(sub.has_active_session || (sub.optical_rx_power && sub.optical_rx_power !== 'Offline' && sub.optical_rx_power !== '--'));
    const initialDetails = {
      subscriptionId: sub.subscription_id,
      customerId: sub.customer_id,
      customerName: sub.customer_name,
      zoneName: sub.zone_code,
      fdtCode: sub.fdt_code,
      fatCode: sub.fat_code,
      username: sub.username,
      status: sub.status,
      expiresAt: sub.expires_at,
      opticalRxPower: sub.optical_rx_power ? (sub.optical_rx_power.includes('dBm') ? sub.optical_rx_power : `${sub.optical_rx_power} dBm`) : '--',
      hasActiveSession: isSubOnline,
      ontVendor: sub.ont_vendor || null,
      ontSerial: sub.ont_serial || null,
      address: null,
      packageName: null,
      sessionIp: null,
      sessionMac: null,
      alerts: (sub.optical_rx_power && parseFloat(sub.optical_rx_power) <= -27)
        ? ['⚠️ إشارة الضوء ضعيفة (أقل من -27 dBm) - يرجى فحص الكيبل التوصيلي']
        : []
    };
    setModalDetails(initialDetails);

    try {
      const checkRes = await powerService.checkSubscriber(sub.subscription_id);
      if (checkRes?.data) {
        const resData = { ...checkRes.data };
        setModalDetails(resData);

        // Synchronize in-memory local subscriber list so card, modal, and state match
        const mappedPowerState: 'up' | 'critical' | 'down' | 'offline' | 'not_scanned' =
          resData.powerState === 'up' || resData.powerState === 'critical' || resData.powerState === 'down'
            ? resData.powerState
            : (resData.opticalRxPower === 'Offline' ? 'down' : (resData.powerState === 'unavailable' ? 'down' : 'not_scanned'));

        setSubscribers((prevSubs) =>
          prevSubs.map((s) => {
            if (String(s.subscription_id || s.id) === String(sub.subscription_id)) {
              return {
                ...s,
                optical_rx_power: resData.opticalRxPower || s.optical_rx_power,
                optical_tx_power: resData.opticalTxPower || s.optical_tx_power,
                power_state: mappedPowerState,
                ont_vendor: resData.ontVendor || s.ont_vendor,
                ont_serial: resData.ontSerial || s.ont_serial,
                has_active_session: resData.hasActiveSession !== undefined ? resData.hasActiveSession : s.has_active_session,
                check_status: mappedPowerState === 'down' ? 'FAILED' : 'SUCCESS',
                last_checked_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                powerReading: {
                  optical_rx_power: resData.opticalRxPower,
                  power_state: mappedPowerState,
                  updated_at: new Date().toISOString()
                }
              };
            }
            return s;
          })
        );
      }
    } catch (err: any) {
      console.error("Error checking subscriber details:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleFatCardClick = (fdt: string, fat: string) => {
    if (activeFatFilter && activeFatFilter.fdt === fdt && activeFatFilter.fat === fat) {
      setActiveFatFilter(null);
    } else {
      setActiveFatFilter({ fdt, fat });
      setSelectedFdt('all');
      setSelectedFat('all');
    }
  };

  const filteredSubscribers = useMemo(() => {
    const safeSubs = Array.isArray(subscribers) ? subscribers : [];
    return safeSubs.filter(s => {
      // 1. Zone / Region filter
      if (selectedZone !== 'all') {
        const sZone = (s.zone_code || '').toUpperCase().trim();
        const selZone = selectedZone.toUpperCase().trim();
        if (sZone !== selZone && !sZone.includes(selZone) && !selZone.includes(sZone)) return false;
      }

      // 2. Active FAT card click filter
      if (activeFatFilter) {
        const sFdt = (s.fdt_code || '').toUpperCase().replace(/^FDT0*/, '');
        const sFat = (s.fat_code || '').toUpperCase().replace(/^FAT0*/, '');
        const aFdt = (activeFatFilter.fdt || '').toUpperCase().replace(/^FDT0*/, '');
        const aFat = (activeFatFilter.fat || '').toUpperCase().replace(/^FAT0*/, '');
        if (sFdt !== aFdt || sFat !== aFat) return false;
      } else {
        // 3. FDT dropdown filter
        if (selectedFdt !== 'all') {
          const sFdt = (s.fdt_code || '').toUpperCase().replace(/^FDT0*/, '');
          const selFdt = selectedFdt.toUpperCase().replace(/^FDT0*/, '');
          if (sFdt !== selFdt) return false;
        }

        // 4. FAT dropdown filter
        if (selectedFat !== 'all') {
          const sFat = (s.fat_code || '').toUpperCase().replace(/^FAT0*/, '');
          const selFat = selectedFat.toUpperCase().replace(/^FAT0*/, '');
          if (sFat !== selFat) return false;
        }
      }

      if (selectedStatus !== 'all') {
        if (selectedStatus === 'connected' && !s.has_active_session) return false;
        
        const hasScan = Boolean(s.powerReading || (s.power_state && s.power_state !== 'not_scanned' && s.optical_rx_power !== '--'));
        if (selectedStatus === 'scanned' && !hasScan) return false;
        if (selectedStatus === 'not_scanned' && hasScan) return false;

        const isDown = s.power_state === 'down' || s.power_state === 'offline' || s.optical_rx_power === 'Offline' || s.optical_rx_power === 'Down';
        const rxVal = parseFloat(s.optical_rx_power || '');
        const isCrit = s.power_state === 'critical' || (!isDown && hasScan && !isNaN(rxVal) && rxVal <= -27.0);
        const isNormal = s.power_state === 'up' || (!isDown && !isCrit && hasScan && !isNaN(rxVal) && rxVal > -27.0);

        if (selectedStatus === 'normal' && !isNormal) return false;
        if (selectedStatus === 'critical' && !isCrit) return false;
        if (selectedStatus === 'down' && !isDown) return false;
        if (selectedStatus === 'offline' && s.status !== 'Expired') return false;
      }

      if (searchQuery.trim() !== '') {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = (s.customer_name || '').toLowerCase().includes(q);
        const userMatch = (s.username || '').toLowerCase().includes(q);
        const subIdMatch = (s.subscription_id || '').toLowerCase().includes(q);
        const custIdMatch = (s.customer_id || '').toLowerCase().includes(q);
        const fdtMatch = (s.fdt_code || '').toLowerCase().includes(q);
        const fatMatch = (s.fat_code || '').toLowerCase().includes(q);
        const zoneMatch = (s.zone_code || '').toLowerCase().includes(q);
        const serialMatch = (s.ont_serial || '').toLowerCase().includes(q);

        if (!nameMatch && !userMatch && !subIdMatch && !custIdMatch && !fdtMatch && !fatMatch && !zoneMatch && !serialMatch) {
          return false;
        }
      }

      return true;
    });
  }, [subscribers, activeFatFilter, selectedZone, selectedFdt, selectedFat, selectedStatus, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredSubscribers.length / pageSize));

  const paginatedSubscribers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredSubscribers.slice(startIndex, startIndex + pageSize);
  }, [filteredSubscribers, currentPage, pageSize]);

  const handleExportExcel = () => {
    const filename = `تقرير_إشارة_المشتركين_${new Date().toISOString().slice(0, 10)}.csv`;
    const headers = ['الاسم', 'PPPoE Username', 'الكابينة FDT', 'العمود FAT', 'قراءة الإشارة (dBm)', 'حالة الجلسة', 'حالة الطاقة', 'تاريخ الانتهاء', 'آخر فحص'];
    
    const rows = filteredSubscribers.map(s => [
      s.customer_name || '—',
      s.username || '—',
      s.fdt_code || '—',
      s.fat_code || '—',
      s.optical_rx_power || '—',
      s.has_active_session ? 'متصل' : 'غير متصل',
      s.power_state === 'up' ? 'سليم' : s.power_state === 'critical' ? 'حرج' : s.power_state === 'down' ? 'مقطوع/متوقف' : 'منتهي الصلاحية',
      s.expires_at || '—',
      s.updated_at ? new Date(s.updated_at).toLocaleString('ar-IQ') : '—'
    ]);
    
    const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const containerBg = isLight ? '#f1f5f9' : '#060f20';
  const cardBg = isLight ? '#ffffff' : 'rgba(13,31,60,0.6)';
  const cardBorder = isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)';
  const textPrimary = isLight ? '#0f172a' : '#f8fafc';
  const textSecondary = isLight ? '#475569' : '#94a3b8';
  const textMuted = isLight ? '#64748b' : '#64748b';
  const inputBg = isLight ? '#ffffff' : '#0f172a';
  const inputBorder = isLight ? '1px solid #cbd5e1' : '1px solid #334155';
  const inputText = isLight ? '#0f172a' : '#f8fafc';
  const tableHeaderBg = isLight ? '#f8fafc' : 'rgba(0,0,0,0.3)';
  const tableBorder = isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.04)';

  const optionStyle: React.CSSProperties = {
    background: isLight ? '#ffffff' : '#0f172a',
    color: isLight ? '#0f172a' : '#f8fafc',
    padding: '8px'
  };

  return (
    <div style={{ padding: '16px', direction: 'rtl', minHeight: '100%', background: containerBg, color: textPrimary, transition: 'background 0.2s' }}>
      
      {/* Header section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ color: textPrimary, fontSize: '22px', fontWeight: 800, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Activity color="#3b82f6" size={24} />
            مراقبة الباور للمشتركين والأعمدة
          </h1>
          <p style={{ color: textSecondary, fontSize: '13px', margin: '0 0 6px 0' }}>
            مراقبة واستكشاف مستويات الإشارة الضوئية للمشتركين والكابينات والأعمدة مباشرة بدقة 100%.
          </p>

          {/* Cloud Cache Status Chip */}
          {cloudCacheInfo && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.12)',
              border: `1px solid ${isLight ? '#bbf7d0' : 'rgba(16,185,129,0.25)'}`,
              borderRadius: '8px',
              padding: '4px 10px',
              fontSize: '12px',
              color: isLight ? '#166534' : '#6ee7b7',
              fontWeight: 600,
              flexWrap: 'wrap'
            }}>
              <CheckCircle2 size={13} color="#10b981" />
              <span>الكاش السحابي لقراءات الباور (نشط ومحفوظ في السيرفر ✅)</span>
              <span style={{ opacity: 0.5 }}>•</span>
              <span style={{ fontSize: '11px', opacity: 0.9 }}>
                آخر قراءة: {cloudCacheInfo.lastScannedAt ? new Date(cloudCacheInfo.lastScannedAt).toLocaleString('ar-IQ') : '—'}
              </span>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', gap: '8px', width: '100%', maxWidth: '520px', flexWrap: 'wrap' }}>
          <button
            onClick={handleScanCurrentPage}
            disabled={loading || syncing || !canManageTasks}
            title={!canManageTasks ? 'غير مصرح بتشغيل الفحص' : "فحص فوري للـ 25 مشترك المعروضين في الصفحة الحالية"}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              background: isLight ? '#ffffff' : 'rgba(255,255,255,0.04)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '8px',
              padding: '10px 12px',
              color: textPrimary,
              cursor: (!canManageTasks || loading || syncing) ? 'not-allowed' : 'pointer',
              opacity: !canManageTasks ? 0.5 : 1,
              fontWeight: 600,
              fontSize: '12px'
            }}
          >
            <Zap size={14} color="#f59e0b" />
            فحص الصفحة (25)
          </button>

          <button
            onClick={handleStartServerScan}
            disabled={loading || syncing || !canManageTasks}
            title={!canManageTasks ? 'غير مصرح بتشغيل فحص السيرفر' : "بدء فحص شامل للمشتركين النشطين في خلفية السيرفر بدون إبقاء المتصفح معلقاً"}
            style={{
              flex: 1.3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 14px',
              color: '#ffffff',
              cursor: (!canManageTasks || loading || syncing) ? 'not-allowed' : 'pointer',
              opacity: !canManageTasks ? 0.5 : 1,
              fontWeight: 600,
              fontSize: '12px',
              boxShadow: '0 4px 12px rgba(30,64,175,0.3)'
            }}
          >
            <Activity size={14} />
            فحص شامل في السيرفر
          </button>
          
          <button
            onClick={() => fetchData(true)}
            title="تحديث البيانات من السيرفر"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              background: isLight ? '#ffffff' : 'rgba(255,255,255,0.06)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px',
              padding: '10px 12px',
              color: textPrimary,
              cursor: 'pointer'
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Sync Status Banner */}
      {syncStatusMsg && (
        <div style={{
          background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.12)',
          border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(59,130,246,0.3)',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '12px',
          fontSize: '13px',
          color: isLight ? '#1d4ed8' : '#60a5fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{syncStatusMsg}</span>
          <button 
            onClick={() => setSyncStatusMsg(null)}
            style={{ background: 'none', border: 'none', color: isLight ? '#1d4ed8' : '#60a5fa', cursor: 'pointer', fontWeight: 'bold' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Portal Subscriber Fetch Progress Bar */}
      {fetchProgress && (
        <div style={{
          background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.08)',
          border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(59,130,246,0.25)',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '16px',
          fontSize: '13px',
          color: isLight ? '#1e40af' : '#93c5fd'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontWeight: 600 }}>{fetchProgress.message}</span>
            </div>
            <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>
              {fetchProgress.loaded} / {fetchProgress.total} مشترك
            </span>
          </div>
          <div style={{
            background: isLight ? '#dbeafe' : 'rgba(59,130,246,0.15)',
            borderRadius: '4px',
            height: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
              height: '100%',
              width: `${Math.min(100, Math.round((fetchProgress.loaded / Math.max(1, fetchProgress.total)) * 100))}%`,
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }} />
          </div>
        </div>
      )}

      {/* Server Background Full Scan Banner (Cloudflare Workflow Snapshot) */}
      {kpis.isJobActive && (
        <div style={{
          background: isLight ? '#f0fdf4' : 'rgba(16,185,129,0.08)',
          border: isLight ? '1px solid #bbf7d0' : '1px solid rgba(16,185,129,0.25)',
          borderRadius: '10px',
          padding: '14px 18px',
          marginBottom: '16px',
          fontSize: '13px',
          color: isLight ? '#065f46' : '#6ee7b7'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontWeight: 700 }}>
                {activeJob?.status === 'INITIALIZING' ? 'جاري تحضير وتجهيز قائمة المشتركين المؤهلين للفحص في السيرفر...' : 'جاري فحص إشارة المشتركين في السيرفر (Cloudflare Workflow)...'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: 800, fontFamily: 'monospace' }}>
                {kpis.scanned} / {kpis.targetEligible} ({kpis.scannedPct}%)
              </span>
              <button
                onClick={handleCancelServerScan}
                style={{
                  background: isLight ? '#fee2e2' : 'rgba(239,68,68,0.2)',
                  border: isLight ? '1px solid #fecaca' : '1px solid rgba(239,68,68,0.4)',
                  color: '#ef4444',
                  borderRadius: '6px',
                  padding: '3px 10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                إلغاء الفحص ✕
              </button>
            </div>
          </div>
          <div style={{
            background: isLight ? '#d1fae5' : 'rgba(16,185,129,0.15)',
            borderRadius: '4px',
            height: '6px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #10b981, #059669)',
              height: '100%',
              width: `${Math.min(100, Math.round((kpis.scanned / Math.max(1, kpis.targetEligible)) * 100))}%`,
              transition: 'width 0.5s ease',
              borderRadius: '4px'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: isLight ? '#047857' : '#a7f3d0', flexWrap: 'wrap', gap: '8px' }}>
            <span>سليم: {kpis.normal} | حرج: {kpis.critical} | متوقف: {kpis.down} {kpis.failed > 0 ? `| فشل: ${kpis.failed}` : ''}</span>
            <span>المؤهلون حالياً في النظام: {kpis.eligibleSubs} مشترك</span>
          </div>
        </div>
      )}

      {/* Local Page Scan Progress Bar (Fast 25-Subscriber Batch) */}
      {pageScanProgress?.isScanning && (
        <div style={{
          background: isLight ? '#eff6ff' : 'rgba(59,130,246,0.08)',
          border: isLight ? '1px solid #bfdbfe' : '1px solid rgba(59,130,246,0.25)',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '16px',
          fontSize: '13px',
          color: isLight ? '#1e40af' : '#93c5fd'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ fontWeight: 600 }}>جاري فحص إشارة المشتركين في الصفحة الحالية...</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontWeight: 700 }}>
                {pageScanProgress.scanned} / {pageScanProgress.total}
              </span>
              <button
                onClick={handleCancelPageScan}
                style={{
                  background: isLight ? '#fee2e2' : 'rgba(239,68,68,0.2)',
                  border: isLight ? '1px solid #fecaca' : '1px solid rgba(239,68,68,0.4)',
                  color: '#ef4444',
                  borderRadius: '6px',
                  padding: '3px 10px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                إلغاء ✕
              </button>
            </div>
          </div>
          <div style={{
            background: isLight ? '#dbeafe' : 'rgba(59,130,246,0.15)',
            borderRadius: '4px',
            height: '6px',
            overflow: 'hidden'
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
              height: '100%',
              width: `${Math.round((pageScanProgress.scanned / Math.max(1, pageScanProgress.total)) * 100)}%`,
              transition: 'width 0.5s ease',
              borderRadius: '4px'
            }} />
          </div>
        </div>
      )}

      {/* KPI Cards Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '12px',
        marginBottom: '20px'
      }}>
        <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', padding: '14px 16px', textAlign: 'right' }}>
          <div style={{ color: textMuted, fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>عدد البوكسات (FAT)</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: textPrimary }}>{kpis.totalSplitters}</div>
          <div style={{ color: textMuted, fontSize: '10px', marginTop: '2px' }}>نشط وموزع جغرافياً</div>
        </div>

        <div 
          onClick={() => { setSelectedStatus('all'); setActiveFatFilter(null); setSelectedFdt('all'); setSelectedFat('all'); }}
          style={{ 
            background: (selectedStatus === 'all' && !activeFatFilter && selectedFdt === 'all' && selectedFat === 'all') ? (isLight ? '#dbeafe' : 'rgba(59,130,246,0.15)') : cardBg, 
            border: (selectedStatus === 'all' && !activeFatFilter && selectedFdt === 'all' && selectedFat === 'all') ? '2px solid #3b82f6' : cardBorder, 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: textMuted, fontSize: '11px', fontWeight: 600, marginBottom: '4px' }}>عدد المشتركين الكلي</div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#3b82f6' }}>{kpis.totalSubscribers}</div>
          <div style={{ color: textSecondary, fontSize: '10px', marginTop: '2px' }}>المؤهلون للفحص: {kpis.eligibleSubs}</div>
        </div>

        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'connected' ? 'all' : 'connected')}
          style={{ 
            background: selectedStatus === 'connected' ? (isLight ? '#d1fae5' : 'rgba(16,185,129,0.2)') : (isLight ? '#f0fdf4' : 'rgba(13,31,60,0.6)'), 
            border: selectedStatus === 'connected' ? '2px solid #10b981' : (isLight ? '1px solid #a7f3d0' : cardBorder), 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>المشتركين المتصلين</span>
            <Activity size={14} />
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#10b981' }}>{kpis.connectedSessions}</div>
          <div style={{ color: isLight ? '#047857' : '#34d399', fontSize: '10px', marginTop: '2px' }}>جلسة إنترنت نشطة (Online)</div>
        </div>

        {/* Card: تم فحصهم */}
        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'scanned' ? 'all' : 'scanned')}
          style={{ 
            background: selectedStatus === 'scanned' ? (isLight ? '#dbeafe' : 'rgba(59,130,246,0.2)') : cardBg, 
            border: selectedStatus === 'scanned' ? '2px solid #3b82f6' : cardBorder, 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#3b82f6', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>تم فحصهم</span>
            <CheckCircle2 size={14} />
          </div>
          <div style={{ fontSize: '20px', fontWeight: 800, color: '#3b82f6' }}>
            {kpis.scanned} <span style={{ fontSize: '11px', fontWeight: 600, color: textSecondary }}>/ {kpis.targetEligible} مؤهل</span>
          </div>
          <div style={{ color: isLight ? '#1d4ed8' : '#93c5fd', fontSize: '10px', marginTop: '2px' }}>
            {kpis.scannedPct}% من المشتركين المؤهلين {kpis.isJobActive && `(المؤهلون حالياً: ${kpis.eligibleSubs})`}
          </div>
        </div>

        {/* Card: غير مؤهلين / Offline */}
        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'not_scanned' ? 'all' : 'not_scanned')}
          style={{ 
            background: selectedStatus === 'not_scanned' ? (isLight ? '#f1f5f9' : 'rgba(100,116,139,0.2)') : cardBg, 
            border: selectedStatus === 'not_scanned' ? '2px solid #64748b' : cardBorder, 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#64748b', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>غير مؤهلين / Offline</span>
            <Clock size={14} />
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#64748b' }}>{kpis.notEligible}</div>
          <div style={{ color: textMuted, fontSize: '10px', marginTop: '2px' }}>اشتراك منتهي أو جهاز مفصول</div>
        </div>

        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'normal' ? 'all' : 'normal')}
          style={{ 
            background: selectedStatus === 'normal' ? (isLight ? '#dcfce7' : 'rgba(16,185,129,0.15)') : (isLight ? '#f0fdf4' : 'rgba(13,31,60,0.6)'), 
            border: selectedStatus === 'normal' ? '2px solid #10b981' : (isLight ? '1px solid #bbf7d0' : cardBorder), 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#10b981', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>إشارة سليمة</span>
            <Zap size={14} />
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#10b981' }}>{kpis.normal}</div>
          <div style={{ color: '#047857', fontSize: '10px', marginTop: '2px' }}>أعلى من -27 dBm</div>
        </div>

        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'critical' ? 'all' : 'critical')}
          style={{ 
            background: selectedStatus === 'critical' ? (isLight ? '#fef3c7' : 'rgba(245,158,11,0.15)') : (isLight ? '#fffbeb' : 'rgba(13,31,60,0.6)'), 
            border: selectedStatus === 'critical' ? '2px solid #f59e0b' : (isLight ? '1px solid #fde68a' : cardBorder), 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#f59e0b', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>إشارة حرجة</span>
            <AlertTriangle size={14} />
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#f59e0b' }}>{kpis.critical}</div>
          <div style={{ color: '#b45309', fontSize: '10px', marginTop: '2px' }}>أقل من -27 dBm</div>
        </div>

        <div 
          onClick={() => setSelectedStatus(selectedStatus === 'down' ? 'all' : 'down')}
          style={{ 
            background: selectedStatus === 'down' ? (isLight ? '#fee2e2' : 'rgba(239,68,68,0.15)') : (isLight ? '#fef2f2' : 'rgba(13,31,60,0.6)'), 
            border: selectedStatus === 'down' ? '2px solid #ef4444' : (isLight ? '1px solid #fecaca' : cardBorder), 
            borderRadius: '12px', 
            padding: '14px 16px', 
            textAlign: 'right',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <div style={{ color: '#ef4444', fontSize: '11px', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>متوقف / مقطوع</span>
            <XCircle size={14} />
          </div>
          <div style={{ fontSize: '24px', fontWeight: 800, color: '#ef4444' }}>{kpis.down}</div>
          <div style={{ color: '#b91c1c', fontSize: '10px', marginTop: '2px' }}>مقطوع أو غير متوفر</div>
        </div>
      </div>



      {/* Main Filter Section */}
      <div style={{
        background: cardBg,
        border: cardBorder,
        borderRadius: '12px',
        padding: '14px',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', color: textMuted, marginBottom: '4px', fontWeight: 600 }}>المنطقة / Region</label>
            <select
              value={selectedZone}
              onChange={(e) => { 
                setSelectedZone(e.target.value); 
                setSelectedFdt('all'); 
                setSelectedFat('all'); 
                setActiveFatFilter(null); 
              }}
              style={{ width: '100%', background: inputBg, border: inputBorder, borderRadius: '6px', padding: '8px 10px', color: inputText, fontSize: '12px', outline: 'none' }}
            >
              <option value="all" style={optionStyle}>الكل (جميع المناطق)</option>
              {zoneOptions.map(opt => <option key={opt} value={opt} style={optionStyle}>{opt}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: textMuted, marginBottom: '4px', fontWeight: 600 }}>الكابينة FDT</label>
            <select
              value={selectedFdt}
              onChange={(e) => { setSelectedFdt(e.target.value); setSelectedFat('all'); setActiveFatFilter(null); }}
              style={{ width: '100%', background: inputBg, border: inputBorder, borderRadius: '6px', padding: '8px 10px', color: inputText, fontSize: '12px', outline: 'none' }}
            >
              <option value="all" style={optionStyle}>الكل</option>
              {fdtOptions.map(opt => <option key={opt} value={opt} style={optionStyle}>{opt}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: textMuted, marginBottom: '4px', fontWeight: 600 }}>العمود FAT</label>
            <select
              value={selectedFat}
              onChange={(e) => { setSelectedFat(e.target.value); setActiveFatFilter(null); }}
              style={{ width: '100%', background: inputBg, border: inputBorder, borderRadius: '6px', padding: '8px 10px', color: inputText, fontSize: '12px', outline: 'none' }}
            >
              <option value="all" style={optionStyle}>الكل</option>
              {fatOptions.map(opt => <option key={opt} value={opt} style={optionStyle}>{opt}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: textMuted, marginBottom: '4px', fontWeight: 600 }}>حالة الإشارة والاتصال</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{ width: '100%', background: inputBg, border: inputBorder, borderRadius: '6px', padding: '8px 10px', color: inputText, fontSize: '12px', outline: 'none' }}
            >
              <option value="all" style={optionStyle}>جميع الحالات ({kpis.totalSubscribers})</option>
              <option value="scanned" style={optionStyle}>🔍 تم فحصهم ({kpis.scanned})</option>
              <option value="not_scanned" style={optionStyle}>⚪ لم يتم الفحص بعد ({kpis.notScanned})</option>
              <option value="connected" style={optionStyle}>🟢 المشتركين المتصلين ({kpis.connectedSessions})</option>
              <option value="normal" style={optionStyle}>⚡ إشارة سليمة ({kpis.normal})</option>
              <option value="critical" style={optionStyle}>⚠️ إشارة ضعيفة/حرجة ({kpis.critical})</option>
              <option value="down" style={optionStyle}>🔴 متوقف/مقطوع ({kpis.down})</option>
              <option value="offline" style={optionStyle}>⏳ منتهي الصلاحية (Expired)</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', color: textMuted, marginBottom: '4px', fontWeight: 600 }}>البحث عن حساب أو مشترك</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  placeholder="ابحث بالاسم، PPPoE، الكابينة، رقم العقد..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  style={{ width: '100%', background: inputBg, border: inputBorder, borderRadius: '6px', padding: '8px 28px 8px 28px', color: inputText, fontSize: '12px', outline: 'none', textAlign: 'right' }}
                />
                <Search size={14} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: textMuted }} />
                {searchInput && (
                  <X
                    size={14}
                    onClick={handleClearSearch}
                    style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: textMuted, cursor: 'pointer' }}
                  />
                )}
              </div>
              <button
                onClick={handleSearchSubmit}
                style={{
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'background 0.2s ease'
                }}
              >
                بحث
              </button>
            </div>
          </div>

          <div>
            <button
              onClick={handleExportExcel}
              disabled={filteredSubscribers.length === 0}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: isLight ? '#ecfdf5' : 'rgba(16,185,129,0.15)', border: isLight ? '1px solid #a7f3d0' : '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', padding: '8px 14px', color: '#059669', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}
            >
              <Download size={14} />
              تصدير كـ Excel
            </button>
          </div>
        </div>
      </div>

      {/* Main Subscriber Container Header & View Mode Switcher */}
      <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: tableBorder }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>قائمة المشتركين ({filteredSubscribers.length})</span>
            {isMobile && (
              <span style={{ fontSize: '10px', background: isLight ? '#e0f2fe' : 'rgba(59,130,246,0.15)', color: '#3b82f6', padding: '2px 6px', borderRadius: '4px' }}>
                نمط الموبايل
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => setViewMode('cards')}
              style={{
                background: (viewMode === 'cards' || (viewMode === 'auto' && isMobile)) ? '#3b82f6' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'),
                color: (viewMode === 'cards' || (viewMode === 'auto' && isMobile)) ? '#ffffff' : textSecondary,
                border: 'none',
                borderRadius: '6px',
                padding: '5px 10px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              بطاقات 📱
            </button>
            <button
              onClick={() => setViewMode('table')}
              style={{
                background: (viewMode === 'table' || (viewMode === 'auto' && !isMobile)) ? '#3b82f6' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)'),
                color: (viewMode === 'table' || (viewMode === 'auto' && !isMobile)) ? '#ffffff' : textSecondary,
                border: 'none',
                borderRadius: '6px',
                padding: '5px 10px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              جدول 💻
            </button>
          </div>
        </div>

        {loading && subscribers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: textMuted }}>جاري جلب قائمة المشتركين...</div>
        ) : filteredSubscribers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '50px', color: textMuted }}>لا يوجد مشتركين مطابقين للبحث أو التصفية الحالية.</div>
        ) : (viewMode === 'cards' || (viewMode === 'auto' && isMobile)) ? (
          /* Mobile Card View - 100% Mobile Responsive */
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {paginatedSubscribers.map((sub, idx) => {
              const isNotScanned = sub.power_state === 'not_scanned' || sub.check_status === 'NOT_SCANNED' || !sub.powerReading || sub.optical_rx_power === '--';
              let signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
              let signalBadgeColor = '#64748b';
              let signalText = '--';
              let powerStateLabel = 'لم يتم الفحص';
              let powerStateColor = '#64748b';

              if (isNotScanned) {
                signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                signalBadgeColor = '#64748b';
                signalText = '--';
                powerStateLabel = 'لم يتم الفحص';
                powerStateColor = '#64748b';
              } else if (sub.power_state === 'up') {
                signalBadgeBg = isLight ? '#dcfce7' : 'rgba(16,185,129,0.12)';
                signalBadgeColor = '#10b981';
                signalText = sub.optical_rx_power;
                powerStateLabel = '⚡ إشارة سليمة';
                powerStateColor = '#10b981';
              } else if (sub.power_state === 'critical') {
                signalBadgeBg = isLight ? '#fef3c7' : 'rgba(245,158,11,0.12)';
                signalBadgeColor = '#f59e0b';
                signalText = sub.optical_rx_power;
                powerStateLabel = '⚠️ إشارة حرجة';
                powerStateColor = '#f59e0b';
              } else if (sub.power_state === 'down' || sub.power_state === 'offline' || sub.optical_rx_power === 'Offline' || sub.optical_rx_power === 'Down') {
                signalBadgeBg = isLight ? '#fee2e2' : 'rgba(239,68,68,0.12)';
                signalBadgeColor = '#ef4444';
                signalText = sub.optical_rx_power === 'Offline' || sub.optical_rx_power === 'Down' ? 'Offline' : sub.optical_rx_power;
                powerStateLabel = '🔴 متوقف/مقطوع';
                powerStateColor = '#ef4444';
              } else {
                signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                signalBadgeColor = '#64748b';
                signalText = sub.optical_rx_power || '--';
                powerStateLabel = 'لم يتم الفحص';
                powerStateColor = '#64748b';
              }

              return (
                <div
                  key={sub.subscription_id || idx}
                  onClick={() => openSubscriberModal(sub)}
                  style={{
                    background: isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
                    border: tableBorder,
                    borderRadius: '12px',
                    padding: '14px',
                    cursor: 'pointer',
                    boxShadow: isLight ? '0 1px 3px rgba(0,0,0,0.04)' : '0 2px 6px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {/* Card Header: Subscriber Name & Connection Badge */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '14px', color: textPrimary, marginBottom: '2px', lineHeight: '1.4' }}>
                        {sub.customer_name}
                      </div>
                      <div style={{ fontSize: '11px', color: textMuted }}>
                        عقد: #{sub.subscription_id}
                      </div>
                    </div>
                    <span style={{
                      color: sub.has_active_session ? '#10b981' : textMuted,
                      fontSize: '11px',
                      fontWeight: 700,
                      background: sub.has_active_session ? (isLight ? '#dcfce7' : 'rgba(16,185,129,0.12)') : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.04)'),
                      padding: '4px 10px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap'
                    }}>
                      {sub.has_active_session ? '🟢 متصل' : '🔴 منفصل'}
                    </span>
                  </div>

                  {/* Network Elements Badge Row */}
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '6px', 
                    marginBottom: '10px',
                    padding: '8px 10px',
                    background: isLight ? '#f8fafc' : 'rgba(0,0,0,0.2)',
                    borderRadius: '8px',
                    border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.04)'
                  }}>
                    <span style={{ background: isLight ? '#e0f2fe' : 'rgba(14,165,233,0.12)', color: isLight ? '#0369a1' : '#38bdf8', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                      المنطقة: {sub.zone_code || 'FNS'}
                    </span>
                    <span style={{ background: isLight ? '#f3e8ff' : 'rgba(168,85,247,0.12)', color: isLight ? '#7e22ce' : '#c084fc', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                      الكابينة: {sub.fdt_code}
                    </span>
                    <span style={{ background: isLight ? '#fef3c7' : 'rgba(245,158,11,0.12)', color: isLight ? '#b45309' : '#fbbf24', padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                      العمود: {sub.fat_code}
                    </span>
                    <span style={{ direction: 'ltr', background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', color: textSecondary, padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                      {sub.username}
                    </span>
                  </div>

                  {/* Card Footer: Power Measurement & State */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '11px', color: textMuted, fontWeight: 600 }}>الإشارة الضوئية:</span>
                      <span style={{
                        background: signalBadgeBg,
                        color: signalBadgeColor,
                        padding: '3px 8px',
                        borderRadius: '6px',
                        fontFamily: 'monospace',
                        fontWeight: 800,
                        fontSize: '12px'
                      }}>
                        {signalText}
                      </span>
                    </div>

                    <span style={{
                      color: powerStateColor,
                      fontWeight: 700,
                      fontSize: '12px'
                    }}>
                      {powerStateLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Desktop Table View */
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: tableHeaderBg, borderBottom: tableBorder }}>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600 }}>اسم المشترك الصريح</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600 }}>المنطقة التابع لها</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600 }}>الكابينة FDT</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600 }}>العمود FAT</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600 }}>PPPoE Username</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600, textAlign: 'center' }}>مستوى الإشارة</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600, textAlign: 'center' }}>حالة الاتصال</th>
                  <th style={{ padding: '12px 16px', color: textSecondary, fontWeight: 600, textAlign: 'center' }}>حالة الفحص / الطاقة</th>
                </tr>
              </thead>
              <tbody>
                {paginatedSubscribers.map((sub, idx) => {
                  const isNotScanned = sub.power_state === 'not_scanned' || sub.check_status === 'NOT_SCANNED' || !sub.powerReading || sub.optical_rx_power === '--';
                  let signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                  let signalBadgeColor = '#64748b';
                  let signalText = '--';
                  let powerStateLabel = 'لم يتم الفحص';
                  let powerStateColor = '#64748b';

                  if (isNotScanned) {
                    signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                    signalBadgeColor = '#64748b';
                    signalText = '--';
                    powerStateLabel = 'لم يتم الفحص';
                    powerStateColor = '#64748b';
                  } else if (sub.power_state === 'up') {
                    signalBadgeBg = isLight ? '#dcfce7' : 'rgba(16,185,129,0.12)';
                    signalBadgeColor = '#10b981';
                    signalText = sub.optical_rx_power;
                    powerStateLabel = '⚡ إشارة سليمة';
                    powerStateColor = '#10b981';
                  } else if (sub.power_state === 'critical') {
                    signalBadgeBg = isLight ? '#fef3c7' : 'rgba(245,158,11,0.12)';
                    signalBadgeColor = '#f59e0b';
                    signalText = sub.optical_rx_power;
                    powerStateLabel = '⚠️ إشارة حرجة';
                    powerStateColor = '#f59e0b';
                  } else if (sub.power_state === 'down' || sub.power_state === 'offline' || sub.optical_rx_power === 'Offline' || sub.optical_rx_power === 'Down') {
                    signalBadgeBg = isLight ? '#fee2e2' : 'rgba(239,68,68,0.12)';
                    signalBadgeColor = '#ef4444';
                    signalText = sub.optical_rx_power === 'Offline' || sub.optical_rx_power === 'Down' ? 'Offline' : sub.optical_rx_power;
                    powerStateLabel = '🔴 متوقف/مقطوع';
                    powerStateColor = '#ef4444';
                  } else {
                    signalBadgeBg = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                    signalBadgeColor = '#64748b';
                    signalText = sub.optical_rx_power || '--';
                    powerStateLabel = 'لم يتم الفحص';
                    powerStateColor = '#64748b';
                  }

                  return (
                    <tr
                      key={sub.subscription_id || idx}
                      onClick={() => openSubscriberModal(sub)}
                      style={{
                        borderBottom: tableBorder,
                        background: idx % 2 === 0 ? (isLight ? '#f8fafc' : 'rgba(255,255,255,0.01)') : 'transparent',
                        transition: 'background 0.2s',
                        cursor: 'pointer'
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: textPrimary }}>
                        <div>{sub.customer_name}</div>
                        <div style={{ fontSize: '11px', color: textMuted, marginTop: '2px' }}>عقد: #{sub.subscription_id}</div>
                      </td>

                      <td style={{ padding: '12px 16px', color: textSecondary, fontWeight: 500 }}>
                        <span style={{ background: isLight ? '#e0f2fe' : 'rgba(14,165,233,0.12)', color: isLight ? '#0369a1' : '#38bdf8', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                          {sub.zone_code || 'المنطقة الرئيسية'}
                        </span>
                      </td>
                      
                      <td style={{ padding: '12px 16px', color: textPrimary, fontWeight: 500 }}>
                        {sub.fdt_code}
                      </td>
                      
                      <td style={{ padding: '12px 16px', color: textPrimary, fontWeight: 500 }}>
                        {sub.fat_code}
                      </td>

                      <td style={{ padding: '12px 16px', direction: 'ltr', textAlign: 'right', color: textSecondary, fontFamily: 'monospace' }}>
                        {sub.username}
                      </td>
                      
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          background: signalBadgeBg,
                          color: signalBadgeColor,
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          fontSize: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          {signalText}
                        </span>
                      </td>
                      
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          color: sub.has_active_session ? '#10b981' : textMuted,
                          fontSize: '11px',
                          fontWeight: 600,
                          background: sub.has_active_session ? (isLight ? '#dcfce7' : 'rgba(16,185,129,0.08)') : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)'),
                          padding: '3px 8px',
                          borderRadius: '4px'
                        }}>
                          {sub.has_active_session ? '🟢 متصل' : '🔴 منفصل'}
                        </span>
                      </td>

                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <span style={{
                          color: powerStateColor,
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}>
                          {powerStateLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Bar */}
      {filteredSubscribers.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', padding: '12px 16px', background: cardBg, border: cardBorder, borderRadius: '12px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: textSecondary }}>
            <span>عرض:</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }} style={{ background: inputBg, border: inputBorder, borderRadius: '6px', padding: '4px 8px', color: inputText, fontSize: '12px', outline: 'none' }}>
              <option value={10} style={optionStyle}>10</option>
              <option value={25} style={optionStyle}>25</option>
              <option value={50} style={optionStyle}>50</option>
              <option value={100} style={optionStyle}>100</option>
            </select>
            <span>مشترك بالصفحة</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)', color: textPrimary, fontSize: '12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
              <ChevronRight size={15} />
              السابق
            </button>

            <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', background: isLight ? '#ffffff' : 'rgba(255,255,255,0.05)', border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.1)', color: textPrimary, fontSize: '12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
              التالي
              <ChevronLeft size={15} />
            </button>
          </div>
        </div>
      )}

      {/* Subscriber Detail Modal */}
      {selectedSub && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px',
          direction: 'rtl'
        }}>
          <div style={{
            background: isLight ? '#ffffff' : '#0d1f3c',
            border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '650px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            padding: '24px'
          }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: tableBorder, paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Activity size={22} color="#3b82f6" />
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: textPrimary, margin: 0 }}>
                  معلومات المشترك وقوة الإشارة
                </h2>
              </div>
              <button
                onClick={() => { setSelectedSub(null); setModalDetails(null); }}
                style={{ background: 'none', border: 'none', color: textSecondary, cursor: 'pointer', padding: '4px' }}
              >
                <X size={20} />
              </button>
            </div>

            {loadingDetails ? (
              <div style={{ padding: '40px', textAlign: 'center', color: textSecondary, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} color="#3b82f6" />
                <span>جاري فحص الإشارة وجلب معلومات المشترك الحيّة من البوابة...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Alerts Banner */}
                {modalDetails?.alerts && modalDetails.alerts.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {modalDetails.alerts.map((alertText: string, aIdx: number) => (
                      <div key={aIdx} style={{
                        background: alertText.includes('ضعيفة') ? (isLight ? '#fffbeb' : 'rgba(245,158,11,0.15)') : (isLight ? '#fee2e2' : 'rgba(239,68,68,0.15)'),
                        border: alertText.includes('ضعيفة') ? '1px solid #fde68a' : '1px solid #fecaca',
                        color: alertText.includes('ضعيفة') ? '#b45309' : '#b91c1c',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 700
                      }}>
                        {alertText}
                      </div>
                    ))}
                  </div>
                )}

                {/* Card Table Layout */}
                <div style={{ border: cardBorder, borderRadius: '10px', overflow: 'hidden', fontSize: '13px' }}>
                  {/* 1. المعلومات الشخصية */}
                  <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '10px 14px', fontWeight: 700, color: '#3b82f6', borderBottom: tableBorder }}>
                    👤 المعلومات الشخصية
                  </div>
                  <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', borderBottom: tableBorder }}>
                    <div><span style={{ color: textMuted }}>الاسم:</span> <strong style={{ color: textPrimary }}>{modalDetails?.customerName || selectedSub.customer_name}</strong></div>
                    <div><span style={{ color: textMuted }}>رقم الحساب (customerId):</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails?.customerId || selectedSub.customer_id}</strong></div>
                    <div><span style={{ color: textMuted }}>رقم الاشتراك (subscriptionId):</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails?.subscriptionId || selectedSub.subscription_id}</strong></div>
                  </div>

                  {/* 2. الموقع الجغرافي */}
                  <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '10px 14px', fontWeight: 700, color: '#3b82f6', borderBottom: tableBorder }}>
                    📍 الموقع الجغرافي
                  </div>
                  <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', borderBottom: tableBorder }}>
                    <div><span style={{ color: textMuted }}>المنطقة (Zone):</span> <strong style={{ color: textPrimary }}>{modalDetails?.zoneName || selectedSub.zone_code || 'FNS0026'}</strong></div>
                    <div><span style={{ color: textMuted }}>الكابينة FDT:</span> <strong style={{ color: textPrimary }}>{modalDetails?.fdtCode || selectedSub.fdt_code}</strong></div>
                    <div><span style={{ color: textMuted }}>العمود FAT:</span> <strong style={{ color: textPrimary }}>{modalDetails?.fatCode || selectedSub.fat_code}</strong></div>
                    <div><span style={{ color: textMuted }}>العنوان:</span> <strong style={{ color: textPrimary }}>{modalDetails?.address || 'العراق - بغداد'}</strong></div>
                  </div>

                  {/* 3. معلومات الجهاز ONU */}
                  <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '10px 14px', fontWeight: 700, color: '#3b82f6', borderBottom: tableBorder }}>
                    🔌 معلومات الجهاز (ONU/ONT)
                  </div>
                  <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', borderBottom: tableBorder }}>
                    <div><span style={{ color: textMuted }}>اسم المستخدم (Username):</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails?.username || selectedSub.username}</strong></div>
                    <div><span style={{ color: textMuted }}>الشركة المصنعة (ONT Vendor):</span> <strong style={{ color: textPrimary }}>{modalDetails?.ontVendor || selectedSub.ont_vendor || 'N/A'}</strong></div>
                    <div>
                      <span style={{ color: textMuted }}>قوة إشارة ONU (Rx Power):</span>{' '}
                      <strong style={{
                        color: parseFloat(modalDetails?.opticalRxPower || selectedSub.optical_rx_power) < -27 ? '#ef4444' : '#10b981',
                        fontFamily: 'monospace'
                      }}>
                        {modalDetails?.opticalRxPower || selectedSub.optical_rx_power}
                      </strong>
                    </div>
                    <div>
                      <span style={{ color: textMuted }}>حالة الجهاز:</span>{' '}
                      <strong style={{ color: (modalDetails?.hasActiveSession ?? selectedSub.has_active_session) ? '#10b981' : '#ef4444' }}>
                        {(modalDetails?.hasActiveSession ?? selectedSub.has_active_session) ? '🟢 متصل (Online)' : '🔴 غير متصل (Offline)'}
                      </strong>
                    </div>
                    <div><span style={{ color: textMuted }}>الرقم التسلسلي (Serial Number):</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails?.ontSerial || selectedSub.ont_serial || 'N/A'}</strong></div>
                  </div>

                  {/* 4. تفاصيل الجلسة النشطة */}
                  {(modalDetails?.sessionIp || modalDetails?.sessionMac || modalDetails?.totalDownloadedBytes) && (
                    <>
                      <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '10px 14px', fontWeight: 700, color: '#3b82f6', borderBottom: tableBorder }}>
                        🌐 تفاصيل الجلسة النشطة (Active Session)
                      </div>
                      <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', borderBottom: tableBorder }}>
                        {modalDetails?.sessionIp && (
                          <div><span style={{ color: textMuted }}>عنوان الـ IP:</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails.sessionIp}</strong></div>
                        )}
                        {modalDetails?.sessionMac && (
                          <div><span style={{ color: textMuted }}>عنوان MAC:</span> <strong style={{ fontFamily: 'monospace', color: textPrimary }}>{modalDetails.sessionMac}</strong></div>
                        )}
                        {Boolean(modalDetails?.totalUploadedBytes) && (
                          <div><span style={{ color: textMuted }}>البيانات المرفوعة:</span> <strong style={{ color: textPrimary }}>{(modalDetails.totalUploadedBytes / (1024 * 1024 * 1024)).toFixed(2)} GB</strong></div>
                        )}
                        {Boolean(modalDetails?.totalDownloadedBytes) && (
                          <div><span style={{ color: textMuted }}>البيانات المحمّلة:</span> <strong style={{ color: textPrimary }}>{(modalDetails.totalDownloadedBytes / (1024 * 1024 * 1024)).toFixed(2)} GB</strong></div>
                        )}
                      </div>
                    </>
                  )}

                  {/* 5. حالة الاشتراك */}
                  <div style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)', padding: '10px 14px', fontWeight: 700, color: '#3b82f6', borderBottom: tableBorder }}>
                    📋 حالة الاشتراك
                  </div>
                  <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <span style={{ color: textMuted }}>الحالة:</span>{' '}
                      <strong style={{ color: (modalDetails?.status || selectedSub.status) === 'Active' ? '#10b981' : '#ef4444' }}>
                        {(modalDetails?.status || selectedSub.status) === 'Active' ? 'Active (نشط)' : (modalDetails?.status || selectedSub.status)}
                      </strong>
                    </div>
                    <div><span style={{ color: textMuted }}>الباقة:</span> <strong style={{ color: textPrimary }}>{modalDetails?.packageName || 'اشتراك FTTH عالي السرعة'}</strong></div>
                    <div><span style={{ color: textMuted }}>تاريخ الانتهاء:</span> <strong style={{ color: textPrimary }}>{selectedSub.expires_at ? new Date(selectedSub.expires_at).toLocaleDateString('ar-IQ') : '—'}</strong></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerMonitoringPage;
